/**
 * MajorEventSplash — one-time glassmorphism popup for "major" priority
 * announcements from the Unified Communications Hub (Jun 2026).
 * Shares the ['announcements','channels'] query with the ticker banner
 * (one network read). Marked seen per-user server-side, so it appears
 * exactly ONCE per announcement.
 */
import React from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkles, FileText, X, ArrowLeft, PartyPopper } from 'lucide-react';
import { useApiQuery } from '../hooks/useApiQuery';
import { useAuth } from '../contexts/AuthContext';
import { canRoleAccessLink } from '../lib/announcementLinks';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const GOLD = '#E5C158';

const renderRich = (text) => {
  const parts = String(text || '').split(/\*\*(.+?)\*\*/g);
  return parts.map((seg, i) =>
    i % 2 === 1
      ? <strong key={i} style={{ color: GOLD }} className="font-bold">{seg}</strong>
      : <React.Fragment key={i}>{seg}</React.Fragment>
  );
};

export const MajorEventSplash = () => {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data } = useApiQuery(
    ['announcements', 'channels', user?.id],
    '/unified-announcements/my/channels',
    { staleTime: 60 * 1000, silent: true },
  );
  const splash = data?.splash;
  const [dismissedId, setDismissedId] = React.useState(null);

  if (!splash || splash.id === dismissedId) return null;
  const isCelebration = splash.priority === 'celebration';

  const close = async () => {
    setDismissedId(splash.id);
    try {
      await axios.post(`${API_URL}/api/unified-announcements/${splash.id}/splash-seen`, {}, { withCredentials: true });
      qc.invalidateQueries({ queryKey: ['announcements', 'channels'] });
    } catch { /* fail-quiet — local dismiss already hides it */ }
  };

  return createPortal(
    <div
      dir="rtl"
      className="fixed inset-0 z-[120] flex items-center justify-center p-4 mesp-splash-overlay"
      style={{ background: 'rgba(4,8,22,0.65)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      data-testid="major-event-splash"
    >
      <div
        className="relative w-full max-w-md rounded-3xl overflow-hidden mesp-splash-card"
        style={{
          background: 'linear-gradient(160deg, rgba(18,34,74,0.88) 0%, rgba(10,17,40,0.92) 100%)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(229,193,88,0.45)',
          boxShadow: '0 24px 70px rgba(0,0,0,0.55), 0 0 40px rgba(229,193,88,0.12)',
        }}
      >
        {/* gold hairline */}
        <div className="h-[3px]" style={{ background: `linear-gradient(90deg, transparent, ${GOLD} 30%, #C99A2F 70%, transparent)` }} />

        <button
          onClick={close}
          className="absolute top-3 start-3 p-1.5 rounded-full text-white/50 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="إغلاق"
          data-testid="splash-close-x"
        >
          <X size={16} />
        </button>

        <div className="p-6 sm:p-8 text-center">
          <span
            className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 mesp-splash-icon"
            style={{ background: 'rgba(229,193,88,0.14)', border: '1px solid rgba(229,193,88,0.5)' }}
          >
            {isCelebration
              ? <PartyPopper size={28} style={{ color: '#4ADE80' }} />
              : <Sparkles size={28} style={{ color: GOLD }} />}
          </span>

          <p className="text-[11px] font-bold tracking-widest mb-2" style={{ color: isCelebration ? '#4ADE80' : GOLD }}>
            {isCelebration ? 'تهنئة من إدارة MESP' : 'إعلان من إدارة MESP'}
          </p>

          <h2 className="text-xl sm:text-2xl font-extrabold text-white leading-snug mb-3" data-testid="splash-title">
            {splash.title}
          </h2>

          {splash.attachment_url && splash.attachment_kind === 'image' && (
            <img
              src={`${API_URL}${splash.attachment_url}`}
              alt="مرفق الإعلان"
              className="w-full max-h-56 object-contain rounded-xl mb-3"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(229,193,88,0.3)' }}
              data-testid="splash-image"
            />
          )}

          <p className="text-sm text-white/85 leading-relaxed whitespace-pre-wrap break-words max-h-52 overflow-y-auto" data-testid="splash-body">
            {renderRich(splash.body)}
          </p>

          {splash.attachment_url && splash.attachment_kind === 'pdf' && (
            <a
              href={`${API_URL}${splash.attachment_url}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-3 px-4 py-2 rounded-lg text-xs font-bold transition-colors"
              style={{ background: 'rgba(239,68,68,0.15)', color: '#FCA5A5', border: '1px solid rgba(239,68,68,0.35)' }}
              data-testid="splash-pdf-link"
            >
              <FileText size={14} /> عرض الملف المرفق (PDF)
            </a>
          )}

          {splash.link_url && canRoleAccessLink(splash.link_url, user?.role) && (
            <a
              href={splash.link_url}
              {...(splash.link_url.startsWith('/') ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
              onClick={close}
              className="mt-4 w-full inline-flex items-center justify-center gap-1.5 py-3 rounded-xl font-bold text-sm transition-transform hover:-translate-y-0.5"
              style={{ background: `linear-gradient(135deg, ${GOLD}, #C99A2F)`, color: '#0A1128', boxShadow: '0 8px 22px rgba(201,154,47,0.35)' }}
              data-testid="splash-link-cta"
            >
              {splash.link_label || 'فتح الرابط'}
              <ArrowLeft size={15} />
            </a>
          )}

          <button
            onClick={close}
            className="mt-3 w-full py-3 rounded-xl font-bold text-sm transition-transform hover:-translate-y-0.5"
            style={splash.link_url && canRoleAccessLink(splash.link_url, user?.role)
              ? { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.18)' }
              : { background: `linear-gradient(135deg, ${GOLD}, #C99A2F)`, color: '#0A1128', boxShadow: '0 8px 22px rgba(201,154,47,0.35)' }}
            data-testid="splash-confirm-btn"
          >
            حسنًا، اطّلعت عليه
          </button>
        </div>
      </div>

      <style>{`
        .mesp-splash-overlay { animation: mesp-splash-fade .35s ease both; }
        .mesp-splash-card { animation: mesp-splash-pop .5s cubic-bezier(.22,1,.36,1) both; }
        .mesp-splash-icon { animation: mesp-splash-glow 2.4s ease-in-out infinite; }
        @keyframes mesp-splash-fade { from { opacity: 0; } to { opacity: 1; } }
        @keyframes mesp-splash-pop {
          from { transform: translateY(24px) scale(.94); opacity: 0; }
          to   { transform: translateY(0)   scale(1);   opacity: 1; }
        }
        @keyframes mesp-splash-glow {
          0%, 100% { box-shadow: 0 0 0 0 rgba(229,193,88,0.35); }
          50%      { box-shadow: 0 0 0 12px rgba(229,193,88,0); }
        }
        @media (prefers-reduced-motion: reduce) {
          .mesp-splash-overlay, .mesp-splash-card, .mesp-splash-icon { animation: none; }
        }
      `}</style>
    </div>,
    document.body
  );
};

export default MajorEventSplash;
